import React from 'react';
import { Provider } from 'react-redux';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AuthProvider } from '@propelauth/react';
import { App } from './app/app';
import { store } from './store/store';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import theme from './theme';
import { NotificationProvider } from './contexts/NotificationContext';
import { VoiceMuteProvider } from './contexts/VoiceContext';

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  //Can include more options, such as disabling autocapture or pageview tracking
};
posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, 
  options
);

(window as any).posthog = posthog;

const AUTH_URL = import.meta.env.VITE_AUTH_URL;

if (!AUTH_URL) {
  throw new Error('VITE_AUTH_URL environment variable is not set');
}

// Only initialize Sentry if DSN is provided and not the default placeholder
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_SENTRY_DSN !== 'your_sentry_dsn') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,

    // Add these new options for local development
    environment: import.meta.env.MODE,
    tracesSampleRate: 1.0,

    // Configure allowed domains for CORS
    allowUrls: ['localhost', '127.0.0.1'],

    // Disable in development mode if needed
    enabled: import.meta.env.MODE === 'production',

    // Debug mode to see what's happening with Sentry
    debug: import.meta.env.MODE === 'development',
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
      // Set a generous timeout for AI processing operations
      networkMode: 'online',
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <PostHogProvider
      client={posthog}
      // Optionally, you can set the initial posthog context here
      // initialPostHogContext={{ distinctId: 'initial-id', properties: {} }
    >
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
          <Provider store={store}>
            <BrowserRouter>
              <AuthProvider authUrl={AUTH_URL}>
                <VoiceMuteProvider>
                  <NotificationProvider>
                    <App />
                  </NotificationProvider>
                </VoiceMuteProvider>
              </AuthProvider>
            </BrowserRouter>
          </Provider>
        </ChakraProvider>
      </QueryClientProvider>
    </PostHogProvider>
  </React.StrictMode>
);
